import React, { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import { CBadge, CSmartTable } from '@coreui/react-pro'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import RowActionsMenu from 'src/components/common/RowActionsMenu'

const SEARCH_FIELDS = [
  { key: 'full_name', label: 'Member' },
  { key: 'email', label: 'Email' },
]

// payments.is_paid — 0 = awaiting an admin decision, 1 = approved, 2 = rejected.
const PENDING = 0
const APPROVED = 1
const REJECTED = 2

// entry_additional_info.confirmation_type — 1 = Admin Approval, 2 = Automatically Approved.
const AUTOMATIC_APPROVAL = 2

// Under "Automatically Approved" the member is let in the moment they submit, so a row that
// was never explicitly rejected is already approved regardless of what is_paid still holds.
const effectiveStatus = (item, isAutoApprove) => {
  const status = Number(item.is_paid)
  if (isAutoApprove && status !== REJECTED) return APPROVED
  return status
}

const STATUS_BADGE = {
  [PENDING]: { label: 'Pending', color: 'warning' },
  [APPROVED]: { label: 'Approved', color: 'success' },
  [REJECTED]: { label: 'Rejected', color: 'danger' },
}

const PaidUserRequest = ({ onMetaChange }) => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState({})
  const [searchField, setSearchField] = useState('full_name')
  const [confirmationType, setConfirmationType] = useState(null)

  const isAutoApprove = confirmationType === AUTOMATIC_APPROVAL

  const columns = [
    { label: 'Member', key: 'full_name', filter: false, sorter: true, _style: { width: '22%' } },
    { label: 'Email', key: 'email', filter: false, sorter: true, _style: { width: '30%' } },
    {
      label: 'Submitted',
      key: 'created_date',
      filter: false,
      sorter: true,
      _style: { width: '22%' },
    },
    { label: 'Status', key: 'is_paid', filter: false, sorter: false, _style: { width: '13%' } },
    { key: 'actions', label: 'Actions', filter: false, sorter: false, _style: { width: '13%' } },
  ]

  const fetchData = useCallback(() => {
    setLoading(true)
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    CommonService.getMemberPaidRequest(offset, itemsPerPage, activePage, params)
      .then((result) => {
        setUsers(result.data)
        setConfirmationType(Number(result.confirmation_type))
        if (onMetaChange) {
          onMetaChange({
            summary: result.summary,
            confirmationType: Number(result.confirmation_type),
          })
        }
      })
      .finally(() => setLoading(false))
  }, [activePage, columnFilter, columnSorter, itemsPerPage, onMetaChange])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleApprove = (userId, status) => {
    CommonService.markToPaidRequest(userId, status).then((res) => {
      if (res.success) {
        fetchData()
        ToastComponent(res.message, 'success')
      }
    })
  }

  const applySearch = (value) => {
    setActivePage(1)
    setColumnFilter(value ? { [searchField]: value } : {})
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter({})
  }

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <TableSearchToolbar
        fields={SEARCH_FIELDS}
        field={searchField}
        onFieldChange={handleFieldChange}
        value={columnFilter?.[searchField] || ''}
        onSearch={applySearch}
        placeholder={`Search by ${activeSearchLabel}...`}
      />
      <div className="ge-datatable ge-datatable--no-select">
        <CSmartTable
          columns={columns}
          columnSorter={{ external: true }}
          scopedColumns={{
            full_name: (item) => <td className="fw-semibold">{item.full_name}</td>,
            created_date: (item) => (
              <td>
                {item.created_at ? moment(item.created_at).format('D MMM YYYY, h:mm A') : '-'}
              </td>
            ),
            is_paid: (item) => {
              const status =
                STATUS_BADGE[effectiveStatus(item, isAutoApprove)] || STATUS_BADGE[PENDING]
              return (
                <td>
                  <CBadge color={status.color} shape="rounded-pill" className="px-2 py-1">
                    {status.label}
                  </CBadge>
                  {isAutoApprove && status.label === 'Approved' && (
                    <CBadge color="secondary" shape="rounded-pill" className="px-2 py-1 ms-1">
                      Auto
                    </CBadge>
                  )}
                </td>
              )
            },
            actions: (item) => {
              // Same rule in both approval modes: only offer the decision the row does not
              // already hold. Under automatic approval that leaves Reject on every live row.
              const status = effectiveStatus(item, isAutoApprove)
              return (
                <td>
                  <RowActionsMenu
                    actions={[
                      {
                        label: 'Approve',
                        hidden: status === APPROVED,
                        onClick: () => handleApprove(item.user_id, APPROVED),
                      },
                      {
                        label: 'Reject',
                        danger: true,
                        color: 'danger',
                        hidden: status === REJECTED,
                        onClick: () => handleApprove(item.user_id, REJECTED),
                      },
                    ]}
                  />
                </td>
              )
            },
          }}
          items={users?.data}
          itemsPerPage={itemsPerPage}
          itemsPerPageSelect
          loading={loading}
          noItemsLabel="No verification requests yet."
          pagination={{ external: true }}
          paginationProps={{
            activePage: activePage,
            pages: Math.ceil(users?.total / itemsPerPage) || 1,
          }}
          tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          onActivePageChange={(page) => setActivePage(page)}
          onItemsPerPageChange={(perPage) => {
            setActivePage(1)
            setItemsPerPage(perPage)
          }}
          onSorterChange={(sorter) => setColumnSorter(sorter)}
        />
      </div>
    </>
  )
}

export default PaidUserRequest
